<script>
  /**
   * @component AssetCard
   * @description A versatile asset card component that displays different types of assets (images, documents, videos)
   * with thumbnails, metadata, and action menus. Supports navigation, copying rules/URLs, downloading, and deletion.
   * Features responsive design with hover states and dropdown actions.
   *
   * @prop {AssetData} data - Asset data object containing metadata like filename, URLs, dimensions, etc.
   * @prop {'image' | 'video' | 'document' | string} type - Asset type determining display format and available actions
   * @prop {() => void} onDelete - Callback function triggered when delete action is selected
   * @prop {boolean} selecting - Whether the card is in selection mode
   * @prop {boolean} selected - Whether this card is currently selected
   * @prop {(data: Object) => void} onSelect - Callback when the card is clicked in selection mode
   *
   * @example
   * <AssetCard
   *   data={{
   *     rulename: 'my-image',
   *     filename: 'photo.jpg',
   *     imageurl: 'https://example.com/photo.jpg',
   *     filesize: 1024
   *   }}
   *   type="image"
   *   onDelete={() => handleDelete()}
   * />
   */
  import { format } from 'svelte-i18n';
  import { useNavigate } from 'svelte-navigator';
  import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from '@sveltestrap/sveltestrap';

  import { Clipboard } from '@client/components/clipboard';
  import { RuleBlock } from '@client/components/rule-block';

  import { downloadFileFromUrl } from '@client/utils/download';
  import { getExtention } from '@client/utils/files';

  import { formatBytes, secondsToHms } from '@common/utils/numbers';

  /**
   * @typedef {Object} ImageMetadata
   * @property {{ width: number, height: number }} [imageinfo]

   * @typedef {Object} AssetData
   * @property {string} rulename
   * @property {string} [filename]
   * @property {string} [imageurl]
   * @property {string} [url]
   * @property {string} [hash]
   * @property {number} [filesize]
   * @property {ImageMetadata} [metadata]
   * @property {string} [thumbnail]
   * @property {string} [title]
   * @property {string} [videotype]
   * @property {number} [width]
   * @property {number} [height]
   * @property {number} [duration]
   */

  /**
   * Asset data for the card, varies by type.
   * @type {AssetData}
   */
  export let data = {};

  /**
   * The type of asset — "image", "video", or "document".
   * @type {'image' | 'video' | 'document' | string}
   */
  export let type = '';

  /**
   * Handler for when the delete button is clicked.
   * @type {() => void}
   */
  export let onDelete = () => {};

  /**
   * Handler for when the rename action is selected. If null, the rename option is hidden.
   * @type {(() => void) | null}
   */
  export let onRename = null;

  /**
   * Whether the card is in selection mode. Renders a checkbox, hides the
   * action dropdown, and makes card clicks toggle selection instead of navigate.
   * @type {boolean}
   */
  export let selecting = false;

  /**
   * Whether this card is currently selected.
   * @type {boolean}
   */
  export let selected = false;

  /**
   * Handler for when the card or its checkbox is clicked in selection mode.
   * @type {(data: Object) => void}
   */
  export let onSelect = () => {};

  /**
   * Whether the card is currently active/selected.
   * @type {boolean}
   */
  let active = false;

  /**
   * Controls whether the dropdown menu is open.
   * @type {boolean}
   */
  let showDropdownMenu = false;

  /** @type {HTMLElement | null} */
  let dropdownRef = null;

  /** @type {HTMLElement | null} */
  let copyRef = null;

  const navigate = useNavigate();

  /**
   * Handles click interaction on the card. If the click is on the dropdown or copy area, it prevents navigation.
   * Otherwise, it navigates to the asset management page for the specific asset type and rulename.
   * @param {MouseEvent} e
   * @returns {false}
   */
  const onClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (selecting) {
      onSelect(data);
      return false;
    }

    const element = e.target;

    active = true;

    if (dropdownRef === element || dropdownRef.contains(element) || copyRef?.contains(element)) {
      active = false;
    } else {
      navigate(`/manage/assets/${type}s/${data.rulename}`);
    }

    return false;
  };

  /**
   * Resets active state on mouse up.
   */
  const onMouseUp = () => {
    active = false;
  };

  /**
   * Cache-busting timestamp for image requests.
   * @type {number}
   */
  const cache = new Date().getTime();

  /**
   * Shortcodes used for rendering rule references.
   * @type {Record<string, string>}
   */
  const shortcode = {
    document: 'doc',
    image: 'img',
    video: 'vid'
  };

  /**
   * Extension-to-color mapping for asset types.
   * @type {Record<string, string>}
   */
  const colors = {
    jpg: 'var(--color-blue-300)',
    jpeg: 'var(--color-blue-300)',
    gif: 'var(--color-green-300)',
    png: 'var(--color-red-300)',
    pdf: 'var(--color-red-300)',
    youtube: 'var(--color-red-300)',
    vimeo: 'var(--color-blue-300)'
  };

  /**
   * The resolved asset URL to use (imageurl or fallback url).
   * @type {string}
   */
  $: assetUrl = data.imageurl || data.url ? data.imageurl || data.url + (data.inline ? '?inline=1' : '') : '';
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<!-- svelte-ignore a11y-click-events-have-key-events -->
<div
  class="asset-card"
  class:active
  class:border-primary={selected}
  class:bg-primary-subtle={selected}
  on:click={onClick}
  on:mouseleave={() => (showDropdownMenu = false)}
  on:mouseup={onMouseUp}
>
  {#if selecting}
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="select" on:click|stopPropagation>
      <input
        class="form-check-input"
        type="checkbox"
        checked={selected}
        on:change={() => onSelect(data)}
        aria-label="{$format('label.SELECT')} {data.rulename}"
      />
    </div>
  {/if}

  {#if !selecting}
    <div bind:this={dropdownRef} class="action">
      <Dropdown isOpen={showDropdownMenu} toggle={() => (showDropdownMenu = !showDropdownMenu)}>
        <DropdownToggle tag="div" class="os-icon-more-horizontal" />
        <DropdownMenu>
          {#if onRename}
            <DropdownItem on:click={onRename}>{$format('label.RENAME')}</DropdownItem>
            <DropdownItem divider />
          {/if}
          <DropdownItem on:click={() => navigate(`/manage/assets/${type}s/${data.rulename}`)}>
            {$format('label.EDIT')}
          </DropdownItem>
          <DropdownItem divider />

          {#if type !== 'image' && data.rulename}
            <Clipboard text="[[{shortcode[type]}:{data.rulename}]]" let:copy>
              <DropdownItem on:click={copy}>{$format('label.COPY_RULE')}</DropdownItem>
            </Clipboard>
          {/if}

          {#if assetUrl}
            <Clipboard text={assetUrl} let:copy>
              <DropdownItem on:click={copy}>{$format('label.COPY_URL')}</DropdownItem>
            </Clipboard>
          {/if}

          {#if data.imageurl}
            <DropdownItem on:click={() => downloadFileFromUrl(data.imageurl)}>{$format('label.DOWNLOAD')}</DropdownItem>
          {/if}
          <DropdownItem divider />
          <DropdownItem on:click={onDelete}>{$format('label.DELETE')}</DropdownItem>
        </DropdownMenu>
      </Dropdown>
    </div>
  {/if}

  <div class="asset">
    {#if type === 'document' || type === 'image'}
      <div class="asset-type" style="background-color: {colors[getExtention(data.filename)]}">
        {getExtention(data.filename)}
      </div>
    {/if}

    {#if type === 'video'}
      <div class="asset-type" style="background-color: {colors[getExtention(data.videotype)]}">
        {getExtention(data.videotype)}
      </div>
    {/if}

    <div class="thumbnail">
      {#if type === 'document'}
        <img
          src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
          style="background: url(https://www.sendergen.com/v2/view/{data.hash}/thumbnail.gif?cache={cache}) no-repeat center center;"
          loading="lazy"
          alt={data.filename}
        />
      {/if}

      {#if type === 'image'}
        <img
          src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
          style:image-rendering={data.metadata?.imageinfo?.width < 32 &&
            data.metadata?.imageinfo?.height < 32 &&
            'pixelated'}
          style="background: url({data.imageurl}?cache={cache}) no-repeat center center;"
          loading="lazy"
          alt={data.rulename}
        />
      {/if}

      {#if type === 'video'}
        <img
          src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
          style="background: url({data.thumbnail}?cache={cache}) no-repeat center center;"
          loading="lazy"
          alt={data.rulename}
        />
      {/if}
    </div>
    <div class="details">
      {#if type !== 'image'}
        <div class="rulename" bind:this={copyRef}>
          <RuleBlock text="[[{shortcode[type]}:{data.rulename}]]" />
        </div>
      {/if}

      {#if type === 'document'}
        <div class="filename">{data.filename}</div>
        <small>{formatBytes(data.filesize)}</small>
      {/if}

      {#if type === 'image'}
        <small class="filename">{data.filename}</small>
        {#if data?.metadata?.imageinfo}
          <small>{data.metadata.imageinfo.width}x{data.metadata.imageinfo.height}</small>
        {/if}
        <small>{formatBytes(data.filesize)}</small>
      {/if}

      {#if type === 'video'}
        <small>{data.title}</small>
        <small>{data.url}</small>
        <small>{data.width}x{data.height}</small>
        <small>{secondsToHms(data.duration)}</small>
      {/if}
    </div>
  </div>
</div>

<style lang="scss" src="./asset-card.scss"></style>
